import { onCleanup, onMount } from "solid-js";
import { glow, type DrawFn } from "./math";

function parseRgb(color: string): [number, number, number] | null {
  const hex = color.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3 || h.length === 4) {
      h = [...h].map((c) => c + c).join("");
    }
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }
  const rgb = color.match(
    /^rgba?\(\s*([0-9.]+)\s*[, ]\s*([0-9.]+)\s*[, ]\s*([0-9.]+)/i,
  );
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

function tintWithAccent(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
) {
  const accent = getComputedStyle(canvas).getPropertyValue("--accent").trim();
  if (!accent) return;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (w < 1 || h < 1) return;
  ctx.save();
  ctx.globalCompositeOperation = "source-atop";
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();

  const rgb = parseRgb(accent);
  if (!rgb) return;
  const [r, g, b] = rgb;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  glow(
    ctx,
    w * 0.5,
    h * 0.42,
    Math.min(w, h) * 0.4,
    `rgba(${r},${g},${b},0.28)`,
  );
  glow(
    ctx,
    w * 0.5,
    h * 0.42,
    Math.min(w, h) * 0.16,
    `rgba(${r},${g},${b},0.4)`,
  );
  ctx.restore();
}

export function FxCanvas(props: { draw: DrawFn }) {
  let canvas!: HTMLCanvasElement;

  onMount(() => {
    const el = canvas;
    const ctx = el.getContext("2d", { alpha: true });
    if (!ctx) return;
    const draw = props.draw;
    let raf = 0;
    let last = performance.now();
    const start = last;
    const tOffset = 0.45;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = el.clientWidth;
      const h = el.clientHeight;
      el.width = Math.max(1, Math.floor(w * dpr));
      el.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const paint = (t: number, dt: number) => {
      draw(ctx, t, el.clientWidth, el.clientHeight, dt);
      tintWithAccent(ctx, el);
    };

    const ro = new ResizeObserver(fit);
    ro.observe(el);
    fit();

    if (reduceMotion) {
      paint(1.2, 0);
      onCleanup(() => {
        ro.disconnect();
      });
      return;
    }

    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      paint((now - start) / 1000 + tOffset, dt);
    };
    raf = requestAnimationFrame(loop);

    onCleanup(() => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    });
  });

  return <canvas ref={canvas} class="fx" aria-hidden="true" />;
}
